import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DenetimKaydiPanel } from '../components/admin/DenetimKaydiPanel';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportTableToPDF, exportTextToPDF } from '../utils/html2pdfExport';
import EmlakExpiryBanner from '../components/EmlakExpiryBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status colors for pie chart
const STATUS_COLORS = {
  not_started: '#6b7280',
  planned: '#06b6d4',
  in_progress: '#3b82f6',
  project_work: '#a855f7',
  tender_phase: '#f97316',
  completed: '#22c55e'
};

// Priority colors for bar chart
const PRIORITY_COLORS = {
  low: '#22c55e',
  medium: '#eab308',
  high: '#f97316',
  urgent: '#ef4444',
  very_urgent: '#ec4899'
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [notes, setNotes] = useState([]);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [gecmisimOpen, setGecmisimOpen] = useState(false);
  const [editNoteDialogOpen, setEditNoteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteFormData, setNoteFormData] = useState({ title: '', content: '' });
  const [deleteNoteId, setDeleteNoteId] = useState(null);
  const [viewNoteDialogOpen, setViewNoteDialogOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState(null);
  const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusFilterTasks, setStatusFilterTasks] = useState([]);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState('');
  const [financeMenuOpen, setFinanceMenuOpen] = useState(false);
  const [grafiklerMenuOpen, setGrafiklerMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bakimOnarimMenuOpen, setBakimOnarimMenuOpen] = useState(false);

  // Hoşgeldin mesajı - sadece ilk giriş
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'Günaydın';
    if (hour >= 12 && hour < 18) return 'İyi Günler';
    if (hour >= 18 && hour < 22) return 'İyi Akşamlar';
    return 'İyi Geceler';
  };

  useEffect(() => {
    const lastWelcome = sessionStorage.getItem('welcome_shown');
    if (!lastWelcome && user) {
      setShowWelcome(true);
      sessionStorage.setItem('welcome_shown', 'true');
      setTimeout(() => setShowWelcome(false), 5000);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
    fetchNotes();
    if (user?.is_admin) {
      fetchPendingCount();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Stats fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/important-notes`, {
        withCredentials: true
      });
      setNotes(response.data);
    } catch (error) {
      console.error('Notes fetch error:', error);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await axios.get(`${API}/pending-tasks`, {
        withCredentials: true
      });
      setPendingCount(response.data.length);
    } catch (error) {
      console.error('Pending count fetch error:', error);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/important-notes`, noteFormData, {
        withCredentials: true
      });
      setAddNoteDialogOpen(false);
      setNoteFormData({ title: '', content: '' });
      fetchNotes();
    } catch (error) {
      console.error('Create note error:', error);
      alert('Not eklenirken hata oluştu');
    }
  };

  const openEditNoteDialog = (note) => {
    setSelectedNote(note);
    setNoteFormData({ title: note.title, content: note.content });
    setEditNoteDialogOpen(true);
  };

  const handleUpdateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/important-notes/${selectedNote.id}`, noteFormData, {
        withCredentials: true
      });
      setEditNoteDialogOpen(false);
      setSelectedNote(null);
      setNoteFormData({ title: '', content: '' });
      fetchNotes();
    } catch (error) {
      console.error('Update note error:', error);
      alert('Not güncellenirken hata oluştu');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/important-notes/${noteId}`, {
        withCredentials: true
      });
      fetchNotes();
    } catch (error) {
      console.error('Delete note error:', error);
      alert('Not silinirken hata oluştu');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Türkçe karakterleri ASCII'ye çevir (PDF için)
  const toAscii = (text) => {
    if (!text) return '';
    const turkishMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U'
    };
    return text.split('').map(char => turkishMap[char] || char).join('');
  };

  const fetchOverdueTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?archived=false`, {
        withCredentials: true
      });
      const now = new Date();
      const overdue = response.data.filter(task => {
        if (task.status === 'completed') return false;
        const dueDate = new Date(task.due_date);
        return dueDate < now;
      });
      setOverdueTasks(overdue);
      setOverdueDialogOpen(true);
    } catch (error) {
      console.error('Overdue tasks fetch error:', error);
      alert('Geciken görevler yüklenirken hata oluştu');
    }
  };

  const fetchTasksByStatus = async (status, label) => {
    try {
      const response = await axios.get(`${API}/tasks?archived=${status === 'completed' ? 'true' : 'false'}`, {
        withCredentials: true
      });
      const filtered = status === 'all' 
        ? response.data 
        : response.data.filter(task => task.status === status);
      setStatusFilterTasks(filtered);
      setSelectedStatusLabel(label);
      setStatusDialogOpen(true);
    } catch (error) {
      console.error('Tasks fetch error:', error);
      alert('Görevler yüklenirken hata oluştu');
    }
  };

  const exportStatusTasksAsPDF = async () => {
    if (!statusFilterTasks || statusFilterTasks.length === 0) {
      alert('Görev bulunamadı');
      return;
    }
    
    try {
      const headers = ['Görev', 'Atanan', 'Öncelik', 'Son Tarih'];
      const tasksData = statusFilterTasks.map(task => [
        task.title || 'Başlık yok',
        task.assigned_to_email || '-',
        task.priority || '-',
        task.due_date ? new Date(task.due_date).toLocaleDateString('tr-TR') : '-'
      ]);

      await exportTableToPDF(headers, tasksData, 
        `Kurumsal İş Yönetimi - ${selectedStatusLabel}`,
        `${selectedStatusLabel.toLowerCase()}-gorevler.pdf`,
        { orientation: 'portrait' }
      );
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  const exportOverdueTasksAsPDF = async () => {
    if (!overdueTasks || overdueTasks.length === 0) {
      alert('Geciken görev bulunamadı');
      return;
    }
    
    try {
      const headers = ['Görev', 'Atanan', 'Durum', 'Son Tarih', 'Gecikme'];
      const tasksData = overdueTasks.map(task => {
        const daysOverdue = task.due_date 
          ? Math.floor((new Date() - new Date(task.due_date)) / (1000 * 60 * 60 * 24))
          : 0;
        return [
          task.title || 'Başlık yok',
          task.assigned_to_email || '-',
          task.status || '-',
          task.due_date ? new Date(task.due_date).toLocaleDateString('tr-TR') : '-',
          daysOverdue + ' gün'
        ];
      });

      await exportTableToPDF(headers, tasksData, 
        'Geciken Görevler',
        'geciken-gorevler.pdf',
        { orientation: 'portrait', headerColor: '#ef4444' }
      );
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  // Prepare data for pie chart
  const statusChartData = stats ? [
    { name: 'Başlanmadı', value: stats.not_started_tasks, color: STATUS_COLORS.not_started },
    { name: 'Planlanan', value: stats.planned_tasks, color: STATUS_COLORS.planned },
    { name: 'Devam Ediyor', value: stats.in_progress_tasks, color: STATUS_COLORS.in_progress },
    { name: 'Proje İşleri', value: stats.project_work_tasks, color: STATUS_COLORS.project_work },
    { name: 'İhale Aşamasında', value: stats.tender_phase_tasks, color: STATUS_COLORS.tender_phase },
    { name: 'Tamamlandı', value: stats.completed_tasks, color: STATUS_COLORS.completed }
  ].filter(item => item.value > 0) : [];

  // Prepare data for bar chart
  const priorityChartData = stats ? [
    { name: 'Düşük', value: stats.low_priority, fill: PRIORITY_COLORS.low },
    { name: 'Orta', value: stats.medium_priority, fill: PRIORITY_COLORS.medium },
    { name: 'Yüksek', value: stats.high_priority, fill: PRIORITY_COLORS.high },
    { name: 'Acil', value: stats.urgent_priority, fill: PRIORITY_COLORS.urgent },
    { name: 'Çok Acil', value: stats.very_urgent_priority, fill: PRIORITY_COLORS.very_urgent }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bg} w-full`}>
      {/* Hoşgeldin Banner */}
      {showWelcome && (
        <div className={`fixed top-0 left-0 right-0 z-50 ${theme.accent} text-white py-3 px-4 text-center shadow-lg animate-in slide-in-from-top duration-500`}>
          <p className="text-lg font-medium">{getGreeting()}, {user?.name}! Hoş geldiniz.</p>
          <button onClick={() => setShowWelcome(false)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}
      {/* Header */}
      <header className={`${theme.header} border-b border-gray-800 backdrop-blur-sm sticky top-0 z-20`}>
        <div className="w-full px-4 py-3">
          {/* Üst satır: Logo + Profil + Mobil Menü */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 object-contain flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-sm md:text-base font-bold text-white truncate">Kurumsal İş Yönetimi Platformu</h1>
                <p className="text-xs text-gray-400 hidden sm:block">Yatırım ve İnşaat</p>
              </div>
            </div>

            {/* Mobil menü butonu */}
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2">
                <div 
                  className="flex items-center gap-2 bg-gray-800/50 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-7 h-7 rounded-full" />
                  <span className="text-sm text-white">{user?.name}</span>
                </div>
                <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-8" data-testid="logout-button">Çıkış</Button>
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-300 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen 
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  }
                </svg>
              </button>
            </div>
          </div>

          {/* Navigasyon - Masaüstü */}
          <nav className="hidden md:flex items-center gap-1 mt-2 flex-wrap">
            <button onClick={() => navigate('/')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md" data-testid="anasayfa-nav-button">Anasayfa</button>
            <div className="h-5 w-px bg-gray-700 mx-1"></div>
            <button onClick={() => navigate('/dashboard')} className="px-3 py-1.5 text-sm text-white bg-blue-600/20 border-b-2 border-blue-500 rounded-t-md">Dashboard</button>
            <button onClick={() => navigate('/tasks')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md" data-testid="tasks-nav-button">Görevler</button>
            <button onClick={() => navigate('/calendar')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md">Takvim</button>
            <button onClick={() => navigate('/archive')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md">Arşiv</button>

            {(user?.is_admin || user?.can_view_grafikler || user?.is_viewer) && (
              <div className="relative">
                <button onClick={() => { setGrafiklerMenuOpen(!grafiklerMenuOpen); setFinanceMenuOpen(false); setBakimOnarimMenuOpen(false); }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md flex items-center gap-1" data-testid="grafikler-menu-btn">
                  Grafikler
                  <svg className={`w-3 h-3 transition-transform ${grafiklerMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {grafiklerMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                    <button onClick={() => { navigate('/grafikler'); setGrafiklerMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-t-lg" data-testid="grafikler-menu-grafikler">📊 Grafikler ve Analizler</button>
                    <button onClick={() => { navigate('/grafikler/sunum-olusturucu'); setGrafiklerMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-b-lg" data-testid="grafikler-menu-sunum">🎬 Sunum Oluşturucu</button>
                  </div>
                )}
              </div>
            )}
            {(user?.is_admin || user?.is_admin_assistant) && (
              <button onClick={() => navigate('/admin/users')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md" data-testid="users-nav-button">Kullanıcılar</button>
            )}
            <button onClick={() => setGecmisimOpen(true)} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md" data-testid="islem-gecmisim-nav-button" title="Kendi giriş/çıkış ve işlem geçmişiniz">🕘 Geçmişim</button>
            {user?.is_admin && (
              <button onClick={() => navigate('/site-admin')} className="px-3 py-1.5 text-sm font-semibold text-amber-400 hover:text-amber-300 hover:bg-amber-600/10 rounded-md" data-testid="admin-panel-nav-button">⚙️ Admin Paneli</button>
            )}

            <div className="h-5 w-px bg-gray-700 mx-1"></div>

            {(user?.is_admin || user?.can_access_finance_module || user?.is_viewer || user?.department === 'yatirim_insaat') && (
              <div className="relative">
                <button onClick={() => { setFinanceMenuOpen(!financeMenuOpen); setBakimOnarimMenuOpen(false); }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md flex items-center gap-1">
                  İhaleler/Ödenek/Hakediş
                  <svg className={`w-3 h-3 transition-transform ${financeMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {financeMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                    <button onClick={() => { navigate('/finance/ihaleler'); setFinanceMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-t-lg">İhaleler</button>
                    <button onClick={() => { navigate('/finance/odenek'); setFinanceMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">Ödenek</button>
                    <button onClick={() => { navigate('/finance/hakedis'); setFinanceMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">Hakediş</button>
                    <button onClick={() => { navigate('/finance/santiye-takip'); setFinanceMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-b-lg">Şantiye Takip</button>
                  </div>
                )}
              </div>
            )}
            {(user?.is_admin || user?.has_bakim_onarim_access) && (
              <div className="relative">
                <button onClick={() => { setBakimOnarimMenuOpen(!bakimOnarimMenuOpen); setFinanceMenuOpen(false); }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md flex items-center gap-1">
                  Bakım-Onarım
                  <svg className={`w-3 h-3 transition-transform ${bakimOnarimMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {bakimOnarimMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-52 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                    <button onClick={() => { navigate('/bakim-onarim/odenek'); setBakimOnarimMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-t-lg">Ödenek</button>
                    <button onClick={() => { navigate('/bakim-onarim/dogrudan-temin'); setBakimOnarimMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">Doğrudan Temin</button>
                    <button onClick={() => { navigate('/bakim-onarim/firma-bilgileri'); setBakimOnarimMenuOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white rounded-b-lg">Firma Bilgileri</button>
                  </div>
                )}
              </div>
            )}
            {(user?.is_admin || user?.has_emlak_kamulastirma_access) && (
              <button onClick={() => navigate('/emlak-kamulastirma')} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-md">Emlak-Kamulaştırma</button>
            )}
            {(user?.is_admin || user?.has_isy_access) && (
              <button onClick={() => navigate('/isy')} className="px-3 py-1.5 text-sm font-semibold text-[#c9a227] hover:text-white hover:bg-[#1e3a5f]/50 rounded-md border border-[#c9a227]/30" data-testid="isy-nav-button">ISY</button>
            )}
            {(user?.is_admin || user?.has_pk_access) && (
              <button onClick={() => navigate('/pk')} className="px-3 py-1.5 text-sm font-semibold text-[#E67E22] hover:text-white hover:bg-[#1B3A5C]/50 rounded-md border border-[#E67E22]/30" data-testid="pk-nav-button">PK</button>
            )}
          </nav>

          {/* Mobil Menü */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-3 pt-3 border-t border-gray-800 space-y-1">
              <div className="flex items-center gap-3 px-3 py-2 mb-2" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}>
                <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                <div>
                  <p className="text-sm text-white">{user?.name}</p>
                  <p className="text-xs text-gray-400">{user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : user?.is_viewer ? 'İzleyici' : 'Kullanıcı'}</p>
                </div>
              </div>
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md" data-testid="anasayfa-mobile-nav">Anasayfa</button>
              <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-blue-400 bg-blue-600/10 rounded-md">Dashboard</button>
              <button onClick={() => { navigate('/tasks'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">Görevler</button>
              <button onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">Takvim</button>
              <button onClick={() => { navigate('/archive'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">Arşiv</button>
              {(user?.is_admin || user?.can_view_grafikler || user?.is_viewer) && (
                <>
                  <button onClick={() => { navigate('/grafikler'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">📊 Grafikler ve Analizler</button>
                  <button onClick={() => { navigate('/grafikler/sunum-olusturucu'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">🎬 Sunum Oluşturucu</button>
                </>
              )}
              {(user?.is_admin || user?.is_admin_assistant) && (
                <button onClick={() => { navigate('/admin/users'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md">Kullanıcılar</button>
              )}
              <button onClick={() => { setGecmisimOpen(true); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md" data-testid="islem-gecmisim-mobile-nav">🕘 Geçmişim</button>
              {user?.is_admin && (
                <button onClick={() => { navigate('/site-admin'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-semibold text-amber-400 hover:bg-gray-800 rounded-md" data-testid="admin-panel-mobile-nav">⚙️ Admin Paneli</button>
              )}
              <div className="border-t border-gray-800 pt-1 mt-1">
                <p className="px-3 py-1 text-xs text-gray-500">Modüller</p>
                {(user?.is_admin || user?.can_access_finance_module || user?.is_viewer || user?.department === 'yatirim_insaat') && (
                  <>
                    <button onClick={() => { navigate('/finance/ihaleler'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">İhaleler</button>
                    <button onClick={() => { navigate('/finance/odenek'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Ödenek</button>
                    <button onClick={() => { navigate('/finance/hakedis'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Hakediş</button>
                    <button onClick={() => { navigate('/finance/santiye-takip'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Şantiye Takip</button>
                  </>
                )}
                {(user?.is_admin || user?.has_bakim_onarim_access) && (
                  <>
                    <button onClick={() => { navigate('/bakim-onarim/odenek'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Bakım-Onarım Ödenek</button>
                    <button onClick={() => { navigate('/bakim-onarim/dogrudan-temin'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Doğrudan Temin</button>
                    <button onClick={() => { navigate('/bakim-onarim/firma-bilgileri'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Firma Bilgileri</button>
                  </>
                )}
                {(user?.is_admin || user?.has_emlak_kamulastirma_access) && (
                  <button onClick={() => { navigate('/emlak-kamulastirma'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-md pl-6">Emlak-Kamulaştırma</button>
                )}
                {(user?.is_admin || user?.has_isy_access) && (
                  <button onClick={() => { navigate('/isy'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-[#c9a227] hover:bg-gray-800 rounded-md pl-6 font-semibold" data-testid="isy-mobile-nav">ISY - Yapım İhaleleri</button>
                )}
                {(user?.is_admin || user?.has_pk_access) && (
                  <button onClick={() => { navigate('/pk'); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-[#E67E22] hover:bg-gray-800 rounded-md pl-6 font-semibold" data-testid="pk-mobile-nav">PK - Periyodik Kontrol</button>
                )}
              </div>
              <div className="border-t border-gray-800 pt-1 mt-1">
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-gray-800 rounded-md">Çıkış</button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 md:px-6 py-6">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Dashboard</h2>
          <p className="text-gray-400">Görev istatistikleriniz ve genel bakış</p>
        </div>

        {/* Emlak Tahsis/Kira Bitiş Hatırlatması (admin veya emlak erişimi olanlar için) */}
        {(user?.is_admin || user?.has_emlak_kamulastirma_access) && (
          <div className="mb-6">
            <EmlakExpiryBanner
              onSelectEmlak={() => navigate('/emlak-kamulastirma/emlak-islemleri')}
            />
          </div>
        )}

        {/* Important Notes Section */}
        {notes.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">📢 Önemli Notlar</h3>
              {user?.is_admin && !user?.is_admin_assistant && (
                <Button
                  onClick={() => setAddNoteDialogOpen(true)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Not Ekle
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {notes.map((note) => (
                <Card key={note.id} className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-700/50">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-yellow-400">{note.title}</CardTitle>
                      {user?.is_admin && !user?.is_admin_assistant && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openEditNoteDialog(note)}
                            size="sm"
                            variant="ghost"
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          <Button
                            onClick={() => handleDeleteNote(note.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </Button>
                        </div>
                      )}
                    </div>
                    <CardDescription className="text-yellow-200/70 mt-2 line-clamp-2">
                      {note.content}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-yellow-300/60">
                        <span>{note.created_by_name}</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(note.created_at).toLocaleDateString('tr-TR')}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            await exportTextToPDF(
                              note.title,
                              note.content,
                              `Oluşturan: ${note.created_by_name} | Tarih: ${new Date(note.created_at).toLocaleDateString('tr-TR')}`,
                              `not-${note.title.substring(0, 30)}.pdf`
                            );
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300"
                          title="PDF İndir"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </Button>
                        <Button
                          onClick={() => {
                            setViewingNote(note);
                            setViewNoteDialogOpen(true);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-yellow-400 hover:text-yellow-300"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Add Note Button (if no notes and admin) */}
        {notes.length === 0 && user?.is_admin && !user?.is_admin_assistant && (
          <Card className="bg-gray-900 border-gray-800 mb-8">
            <CardContent className="py-8 text-center">
              <p className="text-gray-400 mb-4">Henüz önemli not eklenmemiş</p>
              <Button
                onClick={() => setAddNoteDialogOpen(true)}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                İlk Notu Ekle
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 ${user?.is_admin ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-testid="dashboard-stats">
          <Card 
            className="bg-gray-900 border-gray-800 hover:border-blue-700 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('all', 'Tüm Görevler')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Toplam Görev</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-white">{stats?.total_tasks || 0}</div>
              <p className="text-xs text-gray-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-gray-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('not_started', 'Başlanmadı')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Başlanmadı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-gray-500">{stats?.not_started_tasks || 0}</div>
              <p className="text-xs text-gray-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-cyan-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('planned', 'Planlanan')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Planlanan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-cyan-500">{stats?.planned_tasks || 0}</div>
              <p className="text-xs text-cyan-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-blue-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('in_progress', 'Devam Ediyor')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Devam Ediyor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-blue-500">{stats?.in_progress_tasks || 0}</div>
              <p className="text-xs text-blue-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-purple-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('project_work', 'Proje İşleri')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Proje İşleri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-500">{stats?.project_work_tasks || 0}</div>
              <p className="text-xs text-purple-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('tender_phase', 'İhale Aşamasında')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">İhale Aşamasında</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-orange-500">{stats?.tender_phase_tasks || 0}</div>
              <p className="text-xs text-orange-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-green-500 transition-colors cursor-pointer"
            onClick={() => fetchTasksByStatus('completed', 'Tamamlandı')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Tamamlandı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-500">{stats?.completed_tasks || 0}</div>
              <p className="text-xs text-green-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-gray-900 border-gray-800 hover:border-red-700 transition-colors cursor-pointer"
            onClick={fetchOverdueTasks}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-400">Geciken</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-red-500">{stats?.overdue_tasks || 0}</div>
              <p className="text-xs text-red-400 mt-2">Listeyi görmek için tıklayın</p>
            </CardContent>
          </Card>

          {user?.is_admin && (
            <Card 
              className="bg-gray-900 border-gray-800 hover:border-yellow-700 transition-colors cursor-pointer"
              onClick={() => navigate('/admin/pending-tasks')}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-400">Onay Bekleyen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-yellow-500">{pendingCount}</div>
                <p className="text-xs text-yellow-400 mt-2">Onay sayfasına git</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Hızlı İşlemler</CardTitle>
            <CardDescription className="text-gray-400">
              {user?.is_admin ? 'Görevleri yönetin' : 'Görevlerinize göz atın'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={() => navigate('/tasks')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="view-tasks-button"
            >
              Tüm Görevleri Görüntüle
            </Button>
            {user?.is_admin && !user?.is_admin_assistant && (
              <Button
                onClick={() => navigate('/tasks/new')}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
                data-testid="create-task-button"
              >
                Yeni Görev Oluştur
              </Button>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Note Dialog */}
      {/* İşlem Geçmişim Diyalogu */}
      <Dialog open={gecmisimOpen} onOpenChange={setGecmisimOpen}>
        <DialogContent className="bg-gray-950 border-gray-800 text-white max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="islem-gecmisim-dialog">
          <DialogHeader>
            <DialogTitle className="sr-only">İşlem Geçmişim</DialogTitle>
          </DialogHeader>
          {gecmisimOpen && <DenetimKaydiPanel selfOnly />}
        </DialogContent>
      </Dialog>

      <Dialog open={addNoteDialogOpen} onOpenChange={setAddNoteDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Önemli Not Ekle</DialogTitle>
            <DialogDescription className="text-gray-400">
              Tüm kullanıcıların görebileceği önemli bir not ekleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateNote}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="note-title" className="text-white">Başlık *</Label>
                <Input
                  id="note-title"
                  value={noteFormData.title}
                  onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
                  placeholder="Not başlığı"
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="note-content" className="text-white">İçerik *</Label>
                <Textarea
                  id="note-content"
                  value={noteFormData.content}
                  onChange={(e) => setNoteFormData({ ...noteFormData, content: e.target.value })}
                  placeholder="Not içeriği"
                  className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => setAddNoteDialogOpen(false)}
                variant="ghost"
                className="text-gray-400"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Ekle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={editNoteDialogOpen} onOpenChange={setEditNoteDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Önemli Notu Düzenle</DialogTitle>
            <DialogDescription className="text-gray-400">
              Notu güncelleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateNote}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-note-title" className="text-white">Başlık *</Label>
                <Input
                  id="edit-note-title"
                  value={noteFormData.title}
                  onChange={(e) => setNoteFormData({ ...noteFormData, title: e.target.value })}
                  placeholder="Not başlığı"
                  className="bg-gray-800 border-gray-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-note-content" className="text-white">İçerik *</Label>
                <Textarea
                  id="edit-note-content"
                  value={noteFormData.content}
                  onChange={(e) => setNoteFormData({ ...noteFormData, content: e.target.value })}
                  placeholder="Not içeriği"
                  className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  setEditNoteDialogOpen(false);
                  setNoteFormData({ title: '', content: '' });
                }}
                variant="ghost"
                className="text-gray-400"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Güncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Note Dialog */}
      <Dialog open={viewNoteDialogOpen} onOpenChange={setViewNoteDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-yellow-400">{viewingNote?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4 overflow-y-auto flex-1 max-h-[60vh]">
            <p className="text-gray-300 whitespace-pre-wrap break-words">{viewingNote?.content}</p>
            <div className="mt-4 pt-4 border-t border-gray-800 flex items-center justify-between text-xs text-gray-400">
              <span>{viewingNote?.created_by_name}</span>
              <span>{viewingNote && new Date(viewingNote.created_at).toLocaleDateString('tr-TR')}</span>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              onClick={async () => {
                if (!viewingNote) return;
                await exportTextToPDF(
                  viewingNote.title,
                  viewingNote.content,
                  `Oluşturan: ${viewingNote.created_by_name} | Tarih: ${new Date(viewingNote.created_at).toLocaleDateString('tr-TR')}`,
                  `not-${viewingNote.title.substring(0, 30)}.pdf`
                );
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              📄 PDF İndir
            </Button>
            <Button
              onClick={() => setViewNoteDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overdue Tasks Dialog */}
      <Dialog open={overdueDialogOpen} onOpenChange={setOverdueDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-red-400">⚠️ Geciken Görevler ({overdueTasks.length})</DialogTitle>
            <DialogDescription className="text-gray-400">
              Son tarihi geçmiş görevler
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {overdueTasks.map((task) => {
              const daysOverdue = Math.floor((new Date() - new Date(task.due_date)) / (1000 * 60 * 60 * 24));
              return (
                <Card key={task.id} className="bg-red-900/10 border-red-700/30">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-white text-base">{task.title}</CardTitle>
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/50">
                        {daysOverdue} gün gecikmiş
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-sm text-gray-400">{task.description}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">Atanan: </span>
                        <span className="text-gray-300">{task.assigned_to_email}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Son Tarih: </span>
                        <span className="text-red-400">{new Date(task.due_date).toLocaleDateString('tr-TR')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              onClick={exportOverdueTasksAsPDF}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF Olarak İndir
            </Button>
            <Button
              onClick={() => setOverdueDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Filter Tasks Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-400">{selectedStatusLabel} ({statusFilterTasks.length})</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedStatusLabel} durumundaki tüm görevler
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {statusFilterTasks.map((task) => (
              <Card key={task.id} className="bg-gray-800 border-gray-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base">{task.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-gray-400">{task.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Atanan: </span>
                      <span className="text-gray-300">{task.assigned_to_email}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Öncelik: </span>
                      <Badge className="text-xs">{task.priority}</Badge>
                    </div>
                    <div>
                      <span className="text-gray-500">Son Tarih: </span>
                      <span className="text-gray-300">{new Date(task.due_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={exportStatusTasksAsPDF}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF Olarak İndir
            </Button>
            <Button
              onClick={() => setStatusDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
